// Search box with query-syntax autocomplete — one component for every place
// that searches entities (Explore, graph search bar, path pickers).
//
// The server owns the grammar: POST /explore/suggest parses the query, finds
// the token at the caret and returns what fits there (entities, fields such
// as `type:`, values such as `domain`, operators), each with the exact span
// to replace. This component only renders and applies them, so the box and
// the search can never disagree about the syntax.
//
// Keys: ↑/↓ move, Enter picks the highlighted row (or submits), Tab
// completes, Esc closes. Picking an entity calls onPickEntity when given
// (open it / choose it), otherwise its name is inserted as text.
//
// a11y: combobox input + listbox via aria-activedescendant; focus stays in
// the input.
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { suggestEntities, type ExploreSearchResult, type Suggestion, type SuggestResponse } from '../lib/exploreApi';
import { categoryColor } from '../lib/entityColors';
import './QueryInput.css';

const SUGGEST_DEBOUNCE_MS = 110;

const KIND_TITLE: Record<Suggestion['kind'], string> = {
  entity: 'Matches',
  field: 'Filter by',
  value: 'Values',
  operator: 'Combine',
};

const CATEGORY_SHORT: Record<string, string> = {
  actor: 'Actor', family: 'Family', technique: 'ATT&CK', cve: 'CVE', country: 'Country',
};

/** Syntax examples: the "?" cheat sheet and the /search intro. */
export const SEARCH_EXAMPLES: { query: string; detail: string }[] = [
  { query: 'emotet', detail: 'name, alias or value' },
  { query: '"mummy spider"', detail: 'exact phrase' },
  { query: 'type:domain', detail: 'only one type (actor, family, ip, hash, network, …)' },
  { query: 'domain:evil', detail: 'any type works as a field' },
  { query: 'actor:apt country:RU', detail: 'actors by name and country' },
  { query: 'docs>=10', detail: 'at least 10 reports' },
  { query: 'verdict:real', detail: 'IOCs confirmed as real' },
  { query: 'emotet or trickbot', detail: 'either' },
  { query: 'evil -type:url', detail: 'exclude with - or not' },
  { query: 'name:win.*', detail: 'wildcards * and ?' },
];

export interface QueryInputProps {
  /** search: Enter runs the search, empty box shows examples (Explore).
   *  pick: Enter takes the best entity match (graph pickers). */
  mode?: 'search' | 'pick';
  value: string;
  onChange: (value: string) => void;
  /** Enter without a highlighted suggestion. */
  onSubmit?: (value: string) => void;
  /** An entity suggestion was chosen. Without it, the name is inserted. */
  onPickEntity?: (entity: ExploreSearchResult) => void;
  /** Categories this box can't use (e.g. path endpoints drop "country"). */
  excludeCategories?: string[];
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  size?: 'lg' | 'md' | 'sm';
  /** Show the "?" syntax cheat sheet button. */
  showHelp?: boolean;
  /** Extra row at the bottom of the dropdown, e.g. "See all results". */
  footerAction?: { label: string; onSelect: () => void };
  className?: string;
  ariaLabel?: string;
}

export default function QueryInput({
  mode = 'pick', value, onChange, onSubmit, onPickEntity, excludeCategories,
  placeholder = 'Search names, IOCs, CVEs… e.g. emotet, actor:apt country:RU',
  autoFocus, disabled, size = 'md', showHelp = true, footerAction, className, ariaLabel,
}: QueryInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [data, setData] = useState<SuggestResponse | null>(null);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abort = useRef<AbortController | null>(null);
  const pendingCaret = useRef<number | null>(null);
  const excludeKey = (excludeCategories ?? []).join(',');

  const mountedAt = useRef(Date.now());

  const fetchSuggestions = useCallback((q: string, caret: number, immediate = false, show = true) => {
    if (timer.current) clearTimeout(timer.current);
    const run = async () => {
      abort.current?.abort();
      const ctrl = new AbortController();
      abort.current = ctrl;
      setLoading(true);
      try {
        const resp = await suggestEntities(
          { query: q, cursor: caret, exclude_categories: excludeKey ? excludeKey.split(',') : undefined },
          ctrl.signal,
        );
        if (ctrl.signal.aborted) return;
        setData(resp);
        setActive(-1);
        if (show) setOpen(true);
      } catch {
        /* aborted or offline: keep the previous list */
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    };
    if (immediate) void run();
    else timer.current = setTimeout(run, SUGGEST_DEBOUNCE_MS);
  }, [excludeKey]);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    abort.current?.abort();
  }, []);

  // Restore the caret after a programmatic value change (applied suggestion).
  useEffect(() => {
    if (pendingCaret.current != null && inputRef.current) {
      const pos = pendingCaret.current;
      pendingCaret.current = null;
      inputRef.current.setSelectionRange(pos, pos);
    }
  }, [value]);

  // Close on outside click.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setHelpOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const caret = () => inputRef.current?.selectionStart ?? value.length;

  const apply = useCallback((s: Suggestion) => {
    if (s.kind === 'entity' && s.entity && onPickEntity) {
      setOpen(false);
      onPickEntity(s.entity);
      return;
    }
    const after = value.slice(s.end);
    let insert = s.insert;
    if (insert.endsWith(' ') && (after.startsWith(' ') || after === '')) {
      insert = after === '' ? insert : insert.trimEnd();
    }
    const next = value.slice(0, s.start) + insert + after;
    const pos = s.start + insert.length;
    pendingCaret.current = pos;
    onChange(next);
    inputRef.current?.focus();
    // A field ("type:") wants its values next; anything else is complete.
    if (insert.endsWith(':') || insert.endsWith('>=')) fetchSuggestions(next, pos, true);
    else setOpen(false);
  }, [value, onChange, onPickEntity, fetchSuggestions]);

  const runExample = useCallback((q: string) => {
    pendingCaret.current = q.length;
    onChange(q);
    setHelpOpen(false);
    setOpen(false);
    inputRef.current?.focus();
    onSubmit?.(q);
  }, [onChange, onSubmit]);

  const suggestions = data?.suggestions ?? [];
  const examples = !value.trim() && mode === 'search' ? (data?.examples ?? []) : [];
  const rows = suggestions.length + (footerAction ? 1 : 0);
  const showList = open && focused && (suggestions.length > 0 || examples.length > 0 || !!footerAction);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!showList) { fetchSuggestions(value, caret(), true); return; }
      setActive(i => (rows ? (i + 1) % rows : -1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(i => (rows ? (i <= 0 ? rows - 1 : i - 1) : -1));
    } else if (e.key === 'Enter') {
      if (showList && active >= 0 && active < suggestions.length) {
        e.preventDefault();
        apply(suggestions[active]);
      } else if (showList && footerAction && active === suggestions.length) {
        e.preventDefault();
        setOpen(false);
        footerAction.onSelect();
      } else if (mode === 'pick' && onPickEntity && suggestions.some(s => s.kind === 'entity')) {
        // Pickers: Enter takes the best match without arrowing to it.
        e.preventDefault();
        apply(suggestions.find(s => s.kind === 'entity')!);
      } else if (onSubmit) {
        e.preventDefault();
        setOpen(false);
        onSubmit(value);
      }
    } else if (e.key === 'Tab' && showList && suggestions.length > 0 && !e.shiftKey) {
      e.preventDefault();
      apply(suggestions[active >= 0 && active < suggestions.length ? active : 0]);
    } else if (e.key === 'Escape') {
      if (showList || helpOpen) {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        setHelpOpen(false);
      }
    }
  };

  // Group consecutive rows of the same kind under one heading.
  const rendered: React.ReactNode[] = [];
  let lastKind: string | null = null;
  suggestions.forEach((s, i) => {
    if (s.kind !== lastKind) {
      rendered.push(
        <li key={`h-${i}`} className="qi-heading" role="presentation">{KIND_TITLE[s.kind]}</li>,
      );
      lastKind = s.kind;
    }
    const cat = s.entity?.category;
    rendered.push(
      <li
        key={`${s.kind}-${s.label}-${i}`}
        id={`${listId}-${i}`}
        role="option"
        aria-selected={i === active}
        className={`qi-row qi-row--${s.kind}${i === active ? ' qi-row--active' : ''}`}
        onMouseDown={(e) => { e.preventDefault(); apply(s); }}
        onMouseEnter={() => setActive(i)}
      >
        {s.kind === 'entity' && cat && (
          <span className="qi-badge" style={{ '--qi-badge': categoryColor(cat) } as React.CSSProperties}>
            {CATEGORY_SHORT[cat] ?? cat}
          </span>
        )}
        {s.kind === 'field' && <span className="qi-key">{s.label}</span>}
        {s.kind === 'operator' && <span className="qi-key qi-key--op">{s.label}</span>}
        {(s.kind === 'entity' || s.kind === 'value') && <span className="qi-label">{s.label}</span>}
        {s.detail && s.kind !== 'entity' && <span className="qi-detail">{s.detail}</span>}
        {s.count != null && s.count > 0 && (
          <span className="qi-count" title={s.kind === 'entity' ? 'reports' : 'matches'}>
            {s.count.toLocaleString()}
          </span>
        )}
      </li>,
    );
  });

  return (
    <div ref={wrapRef} className={`qi qi--${size}${className ? ` ${className}` : ''}`}>
      <div className="qi-field">
        <svg className="qi-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
          <circle cx="7" cy="7" r="4.5" />
          <line x1="10.5" y1="10.5" x2="14" y2="14" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          className="qi-input"
          value={value}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          spellCheck={false}
          autoComplete="off"
          role="combobox"
          aria-label={ariaLabel ?? 'Search'}
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList && active >= 0 && active < suggestions.length ? `${listId}-${active}` : undefined}
          onChange={(e) => {
            onChange(e.target.value);
            fetchSuggestions(e.target.value, e.target.selectionStart ?? e.target.value.length);
          }}
          onFocus={() => {
            setFocused(true);
            // An autofocus on page load only prefetches; the list opens on
            // the first click or keystroke instead of covering the page.
            const initial = autoFocus && Date.now() - mountedAt.current < 600;
            fetchSuggestions(value, caret(), true, !initial);
          }}
          onBlur={() => setFocused(false)}
          onClick={() => fetchSuggestions(value, caret())}
          onKeyDown={onKeyDown}
          onKeyUp={(e) => {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') {
              fetchSuggestions(value, caret());
            }
          }}
        />
        {loading && <span className="qi-spinner" aria-hidden="true" />}
        {value && !disabled && (
          <button
            type="button"
            className="qi-btn"
            title="Clear"
            aria-label="Clear search"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { onChange(''); inputRef.current?.focus(); fetchSuggestions('', 0, true); }}
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><line x1="3" y1="3" x2="11" y2="11" /><line x1="11" y1="3" x2="3" y2="11" /></svg>
          </button>
        )}
        {showHelp && (
          <button
            type="button"
            className={`qi-btn qi-help-btn${helpOpen ? ' qi-help-btn--on' : ''}`}
            title="Search syntax"
            aria-label="Search syntax help"
            aria-expanded={helpOpen}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setHelpOpen(o => !o); setOpen(false); }}
          >
            ?
          </button>
        )}
      </div>

      {showList && !helpOpen && (
        <ul id={listId} role="listbox" className="qi-list">
          {rendered}
          {examples.length > 0 && (
            <>
              <li className="qi-heading" role="presentation">Try</li>
              {examples.map((ex) => (
                <li
                  key={ex.query}
                  role="presentation"
                  className="qi-row qi-row--example"
                  onMouseDown={(e) => { e.preventDefault(); runExample(ex.query); }}
                >
                  <code className="qi-code">{ex.query}</code>
                  <span className="qi-detail">{ex.detail}</span>
                </li>
              ))}
            </>
          )}
          {footerAction && (
            <li
              id={`${listId}-${suggestions.length}`}
              role="option"
              aria-selected={active === suggestions.length}
              className={`qi-row qi-row--footer${active === suggestions.length ? ' qi-row--active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); setOpen(false); footerAction.onSelect(); }}
              onMouseEnter={() => setActive(suggestions.length)}
            >
              {footerAction.label}
            </li>
          )}
          <li className="qi-hints" role="presentation">
            <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
            <span><kbd>Tab</kbd> complete</span>
            <span><kbd>↵</kbd> {mode === 'pick' ? 'choose' : active >= 0 ? 'select' : 'search'}</span>
            <span><kbd>Esc</kbd> close</span>
          </li>
        </ul>
      )}

      {helpOpen && (
        <div className="qi-help" role="dialog" aria-label="Search syntax">
          <div className="qi-help-title">Search syntax <span className="qi-detail">click an example to try it</span></div>
          <ul>
            {SEARCH_EXAMPLES.map((ex) => (
              <li key={ex.query}>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => runExample(ex.query)}>
                  <code className="qi-code">{ex.query}</code>
                </button>
                <span className="qi-detail">{ex.detail}</span>
              </li>
            ))}
          </ul>
          <div className="qi-help-foot">Words without a field search names. Combine filters freely; everything is optional.</div>
        </div>
      )}
    </div>
  );
}
